import { ViewModeCapabilityCoordinator } from '@mobile-mapping/web3d';
import type {
  DataLoadingService,
  ProjectDataState,
} from '@mobile-mapping/providers';
import { FitToViewOnloadCapabilityCoordinator } from '@trimble/web3d';
import type {
  CapabilityRegistry,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import {
  AlignmentNavigationCapability,
  ClippingCapability,
  ImageCapabilityBase,
} from '@trimble/web3d-capabilities';
import {
  getTrbModelName,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import type { StationInfo, PositioningData } from '@trimble/web3d-providers';
import { Vector3, Quaternion } from '@technology/web3d';
import {
  AlignmentNavigationStatus,
  type StartNavigationOptions,
} from '@technology/web3d-plugin-trimbim';
import { PathNavigationCoordinator } from '@trimble/pane-sync';
import { waitForReactive } from '@trimble/solidjs-utils';
import { partialPointToVector3 } from '@trimble/utilities';
import { LoadState } from '@trimble/data-loading';
import { UnreachableCaseError } from 'ts-essentials';
import type {
  MultipaneCellContentConfig,
  MultipaneReservedData,
  PaneNavigationState,
  SavedStationIdentity,
} from './multipane-view.types';

export const VIEW_LOAD_TIMEOUT_MS = 15_000;

export async function applyTrajectories(
  dataLoadingService: DataLoadingService,
  dataState: ProjectDataState,
  reserved: MultipaneReservedData | null
): Promise<void> {
  if (dataState.trajectory.state !== LoadState.LoadComplete) return;

  const items = dataState.trajectory.items;
  const activeId = reserved?.alignment?.alignmentId;

  // Add the active id so loadExclusive doesn't unload it just to reload.
  const targetIds = new Set(reserved?.loadedTrajectoryIds ?? []);
  if (activeId) targetIds.add(activeId);
  dataLoadingService.loadExclusive('trajectory', Array.from(targetIds));

  if (!activeId || !items[activeId]) return;

  try {
    await waitForReactive(
      () => {
        const i =
          dataState.trajectory.state === LoadState.LoadComplete
            ? dataState.trajectory.items
            : undefined;
        return i?.[activeId]?.loadState;
      },
      (s) => s === LoadState.LoadComplete,
      VIEW_LOAD_TIMEOUT_MS
    );
  } catch {
    return;
  }
}

/**
 * Must run after applyTrajectories and before applyPerPaneViewData so the
 * restored camera isn't overridden by navigation tools starting up.
 */
export async function applyNavigationStates(
  capabilityRegistry: CapabilityRegistry,
  reserved: MultipaneReservedData | null,
  isCurrent: () => boolean = () => true
): Promise<void> {
  if (!reserved?.layout?.paneRows || !isCurrent()) return;

  const { freeView, alignmentNavigation, station } =
    collectPanesByNavigationKind(capabilityRegistry, reserved);
  const alignmentNavigationPanes = alignmentNavigation.map(({ pane }) => pane);
  const trbModelName = reserved.alignment
    ? getTrbModelName(reserved.alignment.alignmentId)
    : null;

  // Reset prior navigation state so each pane matches its saved kind.
  for (const pane of [...freeView, ...alignmentNavigationPanes]) {
    pane
      .get(ViewModeCapabilityCoordinator)()
      ?.stationNavigationController()
      ?.deactivate();
  }
  await Promise.all(
    [...freeView, ...station].map(async (pane) => {
      const cap = pane.get(AlignmentNavigationCapability)();
      if (cap) await exitAlignmentNavigation(cap);
    })
  );
  if (!isCurrent()) return;

  if (trbModelName) {
    await Promise.all(
      alignmentNavigation.map(({ pane, options, camera }) =>
        startAlignmentNavigation(pane, trbModelName, options, camera)
      )
    );
    if (!isCurrent()) return;

    // So panes mounted later (e.g. on layout switch) auto-start navigation.
    capabilityRegistry
      .get(PathNavigationCoordinator)()
      ?.setActiveAlignmentModelId(trbModelName);
  }

  if (station.length > 0) {
    const stationIndex = await resolveSavedStationIndex(
      capabilityRegistry,
      reserved
    );
    if (!isCurrent() || stationIndex == null) return;
    await Promise.all(station.map((pane) => goToStation(pane, stationIndex)));
  }
}

export async function applyPerPaneViewData(
  capabilityRegistry: CapabilityRegistry,
  reserved: MultipaneReservedData | null,
  isCurrent: () => boolean = () => true
): Promise<void> {
  if (!reserved?.layout?.paneRows || !isCurrent()) return;

  const panes = capabilityRegistry.paneContainers();
  const pending: Promise<void>[] = [];

  reserved.layout.paneRows.forEach((row, rowIdx) => {
    row.cells.forEach((cell, cellIdx) => {
      const pane = panes.find(
        (p) => p.paneRowIdx === rowIdx && p.paneCellIdx === cellIdx
      );
      if (!pane) return;

      const viewer = pane.viewerWrapper()?.viewer;
      const viewType = cell.contentConfig.viewMode.type;

      if (viewer && cell.contentConfig.camera) {
        if (viewType === 'Image' || viewType === 'Panorama') {
          // Image/panorama loading resets the camera, so defer until ready.
          // Apply on timeout too so a bad pane doesn't block the load.
          const vmCoord = pane.get(ViewModeCapabilityCoordinator)();
          const activeImgCap = vmCoord
            ?.imageCapabilities()
            ?.capabilities.find((c) => c.isActive());
          if (activeImgCap) {
            pending.push(
              waitForImageReady(activeImgCap)
                .catch(() => undefined)
                .then(() => {
                  if (!isCurrent() || !cell.contentConfig.camera) return;
                  setViewerCamera(viewer, cell.contentConfig.camera);
                })
            );
          }
        } else {
          setViewerCamera(viewer, cell.contentConfig.camera);
        }

        capabilityRegistry
          .get(FitToViewOnloadCapabilityCoordinator)()
          ?.suppressFitToView(pane);
      }

      const clippingCap = pane.get(ClippingCapability)();
      if (clippingCap) {
        clippingCap.loadClippingFromConnectData(
          cell.contentConfig.clipping.sectionPlanes,
          cell.contentConfig.clipping.sectionBox
        );
      }
    });
  });

  if (pending.length > 0) {
    await Promise.all(pending);
  }
}

function setViewerCamera(
  viewer: {
    camera: { position: Vector3; quaternion: Quaternion; fieldOfView: number };
  },
  camera: PositioningData
): void {
  if (camera.cameraPosition) {
    viewer.camera.position = partialPointToVector3(camera.cameraPosition);
  }
  if (camera.quaternion) {
    viewer.camera.quaternion = new Quaternion(...camera.quaternion);
  }
  if (camera.fov != null) viewer.camera.fieldOfView = camera.fov;
}

function waitForImageReady(capability: ImageCapabilityBase): Promise<unknown> {
  return waitForReactive(
    () => capability.isCurrentStationImageReady(),
    (ready) => ready,
    VIEW_LOAD_TIMEOUT_MS
  );
}

async function exitAlignmentNavigation(
  cap: AlignmentNavigationCapability
): Promise<void> {
  if (cap.status() !== AlignmentNavigationStatus.Navigating) return;
  await cap.exitNavigation();
  try {
    await waitForReactive(
      () => cap.status(),
      (s) => s !== AlignmentNavigationStatus.Navigating,
      VIEW_LOAD_TIMEOUT_MS
    );
  } catch {
    // Continue; the next startNavigation will toggle the tool.
  }
}

async function startAlignmentNavigation(
  pane: CapabilityRegistryContainer,
  modelId: string,
  options: StartNavigationOptions,
  savedCamera: PositioningData
): Promise<void> {
  // Registered post view-mode switch, so it may not exist yet.
  const capAccessor = pane.get(AlignmentNavigationCapability);
  const cap =
    capAccessor() ??
    (await waitForReactive(
      capAccessor,
      (c) => c != null,
      VIEW_LOAD_TIMEOUT_MS
    ).catch(() => undefined));
  if (!cap) return;

  if (!cap.isNavigationReady(modelId)) {
    try {
      await waitForReactive(
        () => cap.isNavigationReady(modelId),
        (ready) => ready,
        VIEW_LOAD_TIMEOUT_MS
      );
    } catch {
      return;
    }
  }

  // startNavigation early-returns on the same model, so exit first to
  // re-apply options.
  if (
    cap.status() === AlignmentNavigationStatus.Navigating &&
    cap.alignmentModelId() === modelId
  ) {
    await exitAlignmentNavigation(cap);
  }

  // Pass the saved camera so the tool doesn't briefly frame the chainage
  // default before applyPerPaneViewData restores the saved view.
  await cap.startNavigation(modelId, {
    ...options,
    ...(savedCamera.cameraPosition && {
      cameraPosition: partialPointToVector3(savedCamera.cameraPosition),
    }),
    ...(savedCamera.quaternion && {
      cameraQuaternion: new Quaternion(...savedCamera.quaternion),
    }),
  });
}

function resolveGlobalStationIndex(
  stationsInfo: readonly StationInfo[],
  station: SavedStationIdentity
): number | null {
  let localCount = 0;
  for (let i = 0; i < stationsInfo.length; i++) {
    if (stationsInfo[i].runId === station.runId) {
      if (localCount === station.localStationIndex) {
        return i;
      }
      localCount++;
    }
  }
  return null;
}

function waitForStationsWithRun(
  stationsProvider: StationsCapabilityProvider,
  runId: string
): Promise<readonly StationInfo[]> {
  return waitForReactive(
    () => stationsProvider.stationsInfo(),
    (stations) => stations.some((s) => s.runId === runId),
    VIEW_LOAD_TIMEOUT_MS
  );
}

async function resolveSavedStationIndex(
  capabilityRegistry: CapabilityRegistry,
  reserved: MultipaneReservedData
): Promise<number | null> {
  if (!reserved.station) return null;
  const stationsProvider = capabilityRegistry.get(StationsCapabilityProvider)();
  if (!stationsProvider) return null;
  try {
    const info = await waitForStationsWithRun(
      stationsProvider,
      reserved.station.runId
    );
    return resolveGlobalStationIndex(info, reserved.station);
  } catch {
    return null;
  }
}

async function goToStation(
  pane: CapabilityRegistryContainer,
  globalStationIndex: number
): Promise<void> {
  const vmCoord = pane.get(ViewModeCapabilityCoordinator)();
  if (!vmCoord) return;

  const controller =
    vmCoord.stationNavigationController() ??
    (await waitForReactive(
      () => vmCoord.stationNavigationController(),
      (c) => c != null,
      VIEW_LOAD_TIMEOUT_MS
    ).catch(() => undefined));

  controller?.goToStation(globalStationIndex);
}

interface PanesByNavigationKind {
  freeView: CapabilityRegistryContainer[];
  alignmentNavigation: {
    pane: CapabilityRegistryContainer;
    options: StartNavigationOptions;
    camera: PositioningData;
  }[];
  station: CapabilityRegistryContainer[];
}

function getCellNavigationState(
  contentConfig: MultipaneCellContentConfig
): PaneNavigationState {
  if (contentConfig.navigationState) {
    return contentConfig.navigationState;
  }
  // Image/panorama panes default to station; everything else to free view.
  const type = contentConfig.viewMode.type;
  if (type === 'Image' || type === 'Panorama') {
    return { kind: 'station' };
  }
  return { kind: 'freeView' };
}

function collectPanesByNavigationKind(
  capabilityRegistry: CapabilityRegistry,
  reserved: MultipaneReservedData
): PanesByNavigationKind {
  const panes = capabilityRegistry.paneContainers();
  const buckets: PanesByNavigationKind = {
    freeView: [],
    alignmentNavigation: [],
    station: [],
  };
  reserved.layout.paneRows.forEach((row, rowIdx) => {
    row.cells.forEach((cell, cellIdx) => {
      const pane = panes.find(
        (p) => p.paneRowIdx === rowIdx && p.paneCellIdx === cellIdx
      );
      if (!pane) return;
      const navState = getCellNavigationState(cell.contentConfig);
      switch (navState.kind) {
        case 'freeView':
          buckets.freeView.push(pane);
          break;
        case 'alignmentNavigation':
          buckets.alignmentNavigation.push({
            pane,
            options: navState.options,
            camera: cell.contentConfig.camera,
          });
          break;
        case 'station':
          buckets.station.push(pane);
          break;
        default:
          throw new UnreachableCaseError(navState);
      }
    });
  });
  return buckets;
}
